import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { getTermsStatus, acceptTerms } from '../services/api.js';

/**
 * Blocking modal that requires authenticated users to accept the
 * Terms of Service, Privacy Policy, and Risk Disclaimer before
 * accessing protected areas.
 *
 * Renders nothing if:
 * - User is not authenticated
 * - User has already accepted terms
 * - Terms status is still loading (avoids flash)
 */
export function TermsAcceptanceModal() {
  const { token, isAuthenticated } = useAuth();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [termsStatus, setTermsStatus] = useState<'loading' | 'accepted' | 'not_accepted'>('loading');

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setTermsStatus('accepted'); // Not authenticated — nothing to show
      return;
    }

    getTermsStatus(token)
      .then((data) => {
        setTermsStatus(data.termsAccepted ? 'accepted' : 'not_accepted');
      })
      .catch(() => {
        // Fail open — if we can't check, don't block the user
        setTermsStatus('accepted');
      });
  }, [token, isAuthenticated]);

  async function handleAccept() {
    if (!token || !checked) return;
    setLoading(true);
    setError('');
    try {
      await acceptTerms(token);
      setTermsStatus('accepted');
    } catch {
      setError('Failed to record acceptance. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // Don't show if loading, accepted, or unauthenticated
  if (termsStatus !== 'not_accepted') return null;

  return (
    <div className="ta-modal-overlay">
      <div className="ta-modal">
        <h2 className="ta-modal-title">Terms of Service</h2>
        <p className="ta-modal-text">
          Before continuing, please review and accept our legal agreements.
        </p>

        <div className="ta-modal-links">
          <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          <a href="/disclaimer" target="_blank" rel="noopener noreferrer">Risk Disclaimer</a>
        </div>

        <label className="ta-modal-checkbox-label">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="ta-modal-checkbox"
          />
          <span className="ta-modal-checkbox-text">
            I agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>,{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>, and{' '}
            <a href="/disclaimer" target="_blank" rel="noopener noreferrer">Risk Disclaimer</a>.
          </span>
        </label>

        {error && <p className="ta-modal-error">{error}</p>}

        <button
          className="btn btn-primary ta-modal-button"
          onClick={handleAccept}
          disabled={!checked || loading}
        >
          {loading ? 'Processing...' : 'Accept and Continue'}
        </button>
      </div>
    </div>
  );
}
